import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { RxCross2 } from 'react-icons/rx';
import Toggle from 'react-toggle';
import Dropdown from 'app/components/Dropdown';
import HeaderWithBackButton from 'app/components/HeaderWithBackButton';
import MultiSelectDropdown from 'app/components/MultiSelectDropdown';
import NewAttachment from 'app/components/NewAttachment';
import NewInput from 'app/components/NewInput';
import PageLoader from 'app/components/PageLoader';
import TextArea from 'app/components/Textarea';
import NumberInput from 'app/components/UI/NumberInput';
import Button from 'app/components/button';
import { APP_CONSTANTS } from 'app/constants/app-constants';
import Message from 'app/utils/Message';
import { ParseTableLabelDate } from 'app/utils/Parsers';
import PackageVoucherPanel from './PackageVoucherPanel';
import useCreatePackage, { voucherStoreErrorKey } from './useCreatePackage';

const TOGGLE_CONFIG = [
  { key: 'visible', label: 'Visible on Homepage*' },
  { key: 'status', label: 'Status*' },
];

const formatPoints = (points) =>
  `${Number(points || 0).toLocaleString('en-IN')} pts`;

const formatRange = (startDate, expiryDate) =>
  `${ParseTableLabelDate(startDate)} – ${ParseTableLabelDate(expiryDate)}`;

const CreatePackage = () => {
  const {
    values,
    error,
    handleChange,
    setChange,
    handleTierChange,
    handleMallChange,
    uploadMediaHandler,
    handleSubmit,
    mallList,
    storeList,
    tierIdsOptions,
    voucherTermsConditions,
    vouchers,
    packagePoints,
    removeVoucher,
    panelOpen,
    panelMode,
    panelValues,
    panelError,
    openPanel,
    closePanel,
    handlePanelChange,
    uploadPanelImage,
    handlePanelSubmit,
    isEdit,
    pageLoading,
    navigate,
  } = useCreatePackage();

  const renderRemove = (entry) => {
    const label = `Remove ${entry.voucherTitle || 'voucher'}`;
    // The wrapper keeps a click on the remove control from also opening the row.
    if (entry.everReserved) {
      return (
        <OverlayTrigger
          placement='top'
          overlay={
            <Tooltip id={`remove-${entry.key}`}>
              {Message.Package.ALREADY_BOUGHT_REMOVE}
            </Tooltip>
          }
        >
          <span
            className='d-inline-block'
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type='button'
              className='btn btn-link p-1'
              aria-label={label}
              disabled
              style={{ pointerEvents: 'none', opacity: 0.4 }}
            >
              <RxCross2 />
            </button>
          </span>
        </OverlayTrigger>
      );
    }
    return (
      <span
        className='d-inline-block'
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type='button'
          className='btn btn-link text-danger p-1'
          aria-label={label}
          onClick={() => removeVoucher(entry.key)}
        >
          <RxCross2 />
        </button>
      </span>
    );
  };

  return (
    <>
      <div className='blogsForm'>
        <div className='blogsForm_content'>
          <HeaderWithBackButton
            title={isEdit ? 'Edit Bundle' : 'Create Bundle'}
            goTo={APP_CONSTANTS.VOUCHER}
          />
          <div className='inner-form-wrapper flex justify-between flex-wrap mb-4'>
            <div className='wd-50'>
              <Dropdown
                id='mallId'
                label='Mall name*'
                name='mallId'
                options={mallList}
                placeholder='Select Mall '
                value={values.mallId}
                handleChange={handleMallChange}
                errorText={error?.mallId}
              />
            </div>
            <div className='wd-50'>
              <NewInput
                label='Bundle title*'
                id='title'
                type='text'
                placeholder='Bundle title'
                name='title'
                maxLength={150}
                value={values?.title}
                onChange={handleChange}
                errorText={error?.title}
              />
            </div>
            <div className='wd-100'>
              <TextArea
                label='Description*'
                id='description'
                type='text'
                placeholder='Description'
                name='description'
                maxLength={2000}
                value={values?.description}
                onChange={handleChange}
                errorText={error?.description}
              />
            </div>
            <div className='wd-50'>
              <label>Bundle Promotional Image </label>
              <NewAttachment
                value={values?.promotionalImage}
                id='promotionalImage'
                onFileSelected={uploadMediaHandler}
                fileType='image'
                label='Choose File'
                className='feed_attachment'
                alt='promotionalImage'
                errorText={error?.promotionalImage}
              />
            </div>
            <div className='wd-50'>
              <MultiSelectDropdown
                id='tierIds'
                label='Select Tier'
                name='tierIds'
                options={tierIdsOptions}
                value={values.tierIds}
                onChange={handleTierChange}
                asterisk={true}
              />
              {error?.tierIds && (
                <span className='loginWrapper-valid-text'>{error.tierIds}</span>
              )}
            </div>

            <div className='wd-100'>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                {/* A field group, so it uses the same label treatment as every
                    other field on this form rather than a heading. */}
                <label className='package-members__label'>Bundle Vouchers*</label>
                <Button
                  type='button'
                  className='mt1'
                  variant='Save'
                  name='+ Add vouchers'
                  onClick={() => openPanel()}
                />
              </div>
              <div className='package-members'>
                {vouchers.map((entry, index) => {
                  const storeError = error?.[voucherStoreErrorKey(entry.key)];
                  const noStore = entry.needsStore || !entry.storeName;
                  return (
                    <div key={entry.key} className='mb-2'>
                      <div
                        className='package-member d-flex align-items-center gap-3 border rounded p-2'
                        role='button'
                        tabIndex={0}
                        data-testid={`package-voucher-row-${entry.key}`}
                        onClick={() => openPanel(entry.key)}
                        onKeyDown={(event) => {
                          // Only act on Enter aimed at the row itself. Without
                          // this, Enter on the nested remove button bubbled up
                          // and ALSO opened the edit panel for a voucher that
                          // had just been removed, so saving the panel silently
                          // discarded the edits (WR-02). The remove control
                          // stops click propagation but not keydown.
                          if (
                            event.key === 'Enter' &&
                            event.target === event.currentTarget
                          ) {
                            openPanel(entry.key);
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <span className='package-member__index'>
                          {index + 1}
                        </span>
                        {entry.promotionalImage ? (
                          <img
                            src={entry.promotionalImage}
                            alt=''
                            className='package-member__thumb'
                          />
                        ) : (
                          <span
                            className='package-member__thumb package-member__thumb--empty'
                            aria-hidden='true'
                          />
                        )}
                        <div className='package-member__main'>
                          <div className='package-member__title'>
                            {entry.voucherTitle}
                          </div>
                          <small
                            className={noStore ? 'text-danger' : 'text-muted'}
                          >
                            {noStore ? 'Pick a store' : entry.storeName}
                          </small>
                          {entry.startDate && entry.expiryDate && (
                            <small className='d-block text-muted'>
                              {formatRange(entry.startDate, entry.expiryDate)}
                            </small>
                          )}
                        </div>
                        <div className='package-member__pts'>
                          {formatPoints(entry.loyaltyPoints)}
                        </div>
                        {renderRemove(entry)}
                      </div>
                      {storeError && (
                        <span className='loginWrapper-valid-text'>
                          {storeError}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              {error?.vouchers && (
                <span className='loginWrapper-valid-text'>
                  {error.vouchers}
                </span>
              )}
            </div>

            <div className='wd-50 package-points-field'>
              <NewInput
                label='Bundle Loyalty Points'
                id='packagePoints'
                type='text'
                name='packagePoints'
                value={formatPoints(packagePoints)}
                readOnly
              />
            </div>
            <div className='wd-50'>
              <NumberInput
                label='Bundle Count*'
                id='packageCount'
                placeholder='Enter bundle count'
                name='packageCount'
                value={values?.packageCount}
                onChange={handleChange}
                errorText={error?.packageCount}
              />
            </div>
            <div className='wd-50'>
              <NumberInput
                label='Usage Limit*'
                id='usageLimit'
                placeholder='Enter usage limit'
                name='usageLimit'
                value={values?.usageLimit}
                onChange={handleChange}
                errorText={error?.usageLimit}
              />
            </div>
            <div className='wd-50'>
              <NumberInput
                label='Frequency*'
                id='frequency'
                placeholder='Enter frequency'
                name='frequency'
                value={values?.frequency}
                onChange={handleChange}
                errorText={error?.frequency}
              />
            </div>
            <div className='wd-50'>
              <NumberInput
                label='Sort Order'
                id='sortOrder'
                placeholder='0 = normal (leave blank for no priority)'
                name='sortOrder'
                value={values?.sortOrder}
                onChange={handleChange}
                errorText={error?.sortOrder}
              />
            </div>
            <div className='wd-100'>
              <MultiSelectDropdown
                id='termsOfUsage'
                label='Terms of Usage*'
                name='termsOfUsage'
                options={voucherTermsConditions}
                value={values.termsOfUsage}
                onChange={handleChange}
                asterisk={true}
              />
              {error?.termsOfUsage && (
                <span className='loginWrapper-valid-text'>
                  {error.termsOfUsage}
                </span>
              )}
            </div>
            <div className='wd-100'>
              <TextArea
                label='How To Redeem*'
                id='howToRedeem'
                type='text'
                placeholder='Enter how to redeem'
                name='howToRedeem'
                value={values?.howToRedeem}
                onChange={handleChange}
                errorText={error?.howToRedeem}
              />
            </div>

            <div className='wd-100 toggle-group'>
              {TOGGLE_CONFIG.map(({ key, label }) => (
                <div className='toggle-card' key={key}>
                  <div className='toggle-card_header'>
                    <span className='toggle-card_label tooltip-on-hover'>
                      {label}
                    </span>
                    <span
                      className={`toggle-card_badge ${
                        values[key] ? 'is-on' : 'is-off'
                      }`}
                    >
                      {values[key] ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <Toggle
                    checked={values[key]}
                    icons={false}
                    onChange={() => setChange(key, !values[key])}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className='d-flex justify-content-start align-items-center flex-wrap'>
            <Button
              className='mt1'
              onClick={handleSubmit}
              variant='Save'
              name='Submit'
              isDisabled={pageLoading ? true : false}
            />
            <Button
              className='mt1 cancel-button ms-2'
              onClick={() => navigate(-1)}
              variant='Cancel'
              name='Cancel'
              isDisabled={pageLoading ? true : false}
            />
          </div>
        </div>
        {pageLoading && <PageLoader pageLoading={pageLoading} />}
      </div>
      {/* FilterModel renders its own <form>: a page-level sibling, never a
          child of the field markup above. */}
      <PackageVoucherPanel
        isOpen={panelOpen}
        mode={panelMode}
        values={panelValues}
        error={panelError}
        storeList={storeList}
        onChange={handlePanelChange}
        onUploadImage={uploadPanelImage}
        onSubmit={handlePanelSubmit}
        onClose={closePanel}
      />
    </>
  );
};

export default CreatePackage;
